import React from 'react';
import { Menu, X, Phone, Mail, MapPin } from 'lucide-react';

function App() {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [parallaxY, setParallaxY] = React.useState(0);
  const [isHeaderHidden, setIsHeaderHidden] = React.useState(false);
  const isMenuOpenRef = React.useRef(isMenuOpen);

  React.useEffect(() => {
    isMenuOpenRef.current = isMenuOpen;
  }, [isMenuOpen]);

  React.useEffect(() => {
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    let rafId = null;
    let lastScrollY = window.scrollY || 0;

    const update = () => {
      rafId = null;
      const y = window.scrollY || 0;

      if (isMenuOpenRef.current) {
        setIsHeaderHidden(false);
        lastScrollY = y;
        if (prefersReducedMotion) setParallaxY(0);
        return;
      }

      if (!prefersReducedMotion) {
        const next = Math.min(y * 0.15, 60);
        setParallaxY(next);
      } else {
        setParallaxY(0);
      }

      if (y <= 0) {
        setIsHeaderHidden(false);
      } else if (y > lastScrollY) {
        setIsHeaderHidden(true);
      } else if (y < lastScrollY) {
        setIsHeaderHidden(false);
      }

      lastScrollY = y;
    };

    const onScroll = () => {
      if (rafId != null) return;
      rafId = window.requestAnimationFrame(update);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    update();

    return () => {
      window.removeEventListener('scroll', onScroll);
      if (rafId != null) window.cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white text-gray-800 font-['Lato']" id='start'>
      {/* Navigation - Centered Logo & Links */}
      <nav className="fixed w-full z-50">
        <button
          type="button"
          aria-label={isMenuOpen ? 'Stäng meny' : 'Öppna meny'}
          className={`md:hidden fixed top-10 right-8 z-[60] text-gray-600 transition-all duration-300 ease-out ${isHeaderHidden ? 'rounded-full bg-white/50 backdrop-blur-xl border border-gray-200 shadow-md p-3 text-gray-700' : 'p-2'}`}
          onClick={() => {
            setIsHeaderHidden(false);
            setIsMenuOpen(!isMenuOpen);
          }}
        >
          {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
        </button>

        <div
          className={`bg-white/50 backdrop-blur-xl border-b border-gray-200 transition-transform duration-300 ease-in-out will-change-transform ${isHeaderHidden ? '-translate-y-full' : 'translate-y-0'}`}
        >
          <div className="container mx-auto px-6 py-4 flex flex-col items-center justify-center">

            <div className="flex items-center justify-center space-x-12 w-full">
              {/* Logo Section - Responsive - using provided SVGs */}
              <a className="my-4 md:mb-4 md:mt-2" href="#start">
                <img
                  src="/anesis-long-svg.svg"
                  alt="Anesis Massage & Wellness"
                  className="hidden md:block h-12 w-auto"
                />
                <img
                  src="/anesis-svg.svg"
                  alt="Anesis Massage & Wellness"
                  className="md:hidden h-16 w-auto -translate-x-8"
                />
              </a>
            </div>

            {/* Desktop Menu - Simple Links */}
            <div className="hidden md:flex items-center space-x-12">
              <a href="#behandlingar" className="text-gray-600 hover:text-primary hover:font-bold uppercase tracking-widest text-sm font-medium transition-colors">BEHANDLINGAR</a>
              <a href="#om-oss" className="text-gray-600 hover:text-primary hover:font-bold uppercase tracking-widest text-sm font-medium transition-colors">OM OSS</a>
              <a href="#kontakt" className="text-gray-600 hover:text-primary hover:font-bold uppercase tracking-widest text-sm font-medium transition-colors">KONTAKT</a>
              <a href="https://www.bokadirekt.se/places/anesis-massage-&-wellness-134203" target="_blank" className="px-8 py-3 bg-primary text-white uppercase tracking-widest text-sm font-medium rounded-md shadow-sm hover:shadow-md hover:font-bold transition-shadow duration-200" rel="noopener noreferrer">
                BOKA TID
              </a>
            </div>
          </div>

          {/* Mobile Menu Dropdown */}
          <div
            className={`md:hidden w-full overflow-hidden bg-black/5 backdrop-blur-xl transition-[max-height,opacity,transform] duration-300 ease-out ${isMenuOpen ? 'max-h-[80vh] opacity-100 translate-y-0 border-t border-gray-100' : 'max-h-0 opacity-0 -translate-y-2 border-t border-transparent pointer-events-none'}`}
            aria-hidden={!isMenuOpen}
          >
            <div className="px-6 py-12 space-y-12 flex flex-col text-center">
              <a href="#behandlingar" className="text-lg font-medium text-gray-800" onClick={() => setIsMenuOpen(false)}>BEHANDLINGAR</a>
              <a href="#om-oss" className="text-lg font-medium text-gray-800" onClick={() => setIsMenuOpen(false)}>OM OSS</a>
              <a href="#kontakt" className="text-lg font-medium text-gray-800" onClick={() => setIsMenuOpen(false)}>KONTAKT</a>
              <a href="https://www.bokadirekt.se/places/anesis-massage-&-wellness-134203" target="_blank" className="block w-full py-4 bg-primary text-white font-medium uppercase tracking-widest rounded-md" rel="noopener noreferrer" onClick={() => setIsMenuOpen(false)}>
                BOKA TID
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section - Full Width Image */}
      <header className="relative w-full h-[85vh] min-h-[600px] flex items-center justify-center text-center px-4 pt-48 overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="/massage.jpg"
            alt="Relaxing Massage"
            className="w-full h-full object-cover will-change-transform"
            style={{
              transform: `translate3d(0, ${parallaxY}px, 0) scale(1.1)`,
            }}
          />
          <div className="absolute inset-0 bg-white/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-white via-white/40 to-transparent" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-4xl mx-auto mt-12 animate-in fade-in duration-1000 slide-in-from-bottom-4">
          <h1 className="text-5xl md:text-7xl font-light text-primary mb-6 tracking-wide uppercase">
            Harmonisera <br /> <span className="font-bold">Kropp & Själ</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-700 mb-12 max-w-2xl mx-auto font-light leading-relaxed">
            Professionell massage och fysioterapi.
          </p>
          <div>
            <a href="https://www.bokadirekt.se/places/anesis-massage-&-wellness-134203" target="_blank" className="inline-block px-12 py-4 bg-primary text-white uppercase tracking-widest font-bold rounded-md shadow-lg hover:shadow-xl transform mb-2 transition-shadow duration-200" rel="noopener noreferrer">
              BOKA BEHANDLING
            </a>
          </div>
        </div>
      </header>

      {/* About Section - Simple Text */}
      <section id="om-oss" className="py-32 bg-white">
        <div className="container mx-auto px-6 lg:px-12 max-w-4xl text-center">
          <h2 className="text-sm font-bold text-secondary tracking-[0.2em] uppercase mb-8">OM ANESIS</h2>
          <p className="text-2xl md:text-3xl text-gray-800 font-light mb-12">
            Alla behandlingar utförs av legitimerad fysioterapeut med över 25 års klinisk erfarenhet. Våra behandlingsmetoder som är en kombination av avslappnande och terapeutiska tekniker hjälper dig att behandla smärta och muskelproblem samt må bättre både fysiskt och mentalt.
          </p>
          <div className="w-24 h-1 bg-gray-100 mx-auto rounded-full"></div>
        </div>
      </section>

      {/* Services Section - Clean List Layout */}
      <section id="behandlingar" className="py-24 bg-[#f5f5f5]">
        <div className="container mx-auto px-6 lg:px-12 max-w-5xl">

          <div className="text-center mb-20">
            <h2 className="text-3xl font-light text-primary uppercase tracking-widest mb-4">BEHANDLINGAR</h2>
          </div>

          <div className="space-y-10">
            {/* Service 0 */}
            <div className="bg-white p-8 md:p-12 border-l-4 border-primary flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <h3 className="text-xl font-bold text-primary uppercase tracking-wide">Summer Mojito Massage/<br />Sommarens limited edition-behandling<br /> (juni-augusti)</h3>
                  <span className="bg-primary text-white px-3 py-1 rounded-md text-sm font-bold">40 min</span>
                </div>
                <p className="text-gray-600 leading-relaxed text-lg font-light">
                  En uppfriskande sommarbehandling med fräsch doftande ekologisk mojito-olja som väcker sinnena med toner av lime och mynta. Massagen hjälper kroppen att slappna av samtidigt som huden får näring och en svalkande, energigivande känsla. Perfekt för dig som vill känna dig lätt, återhämtad och sommarfräsch.
                  En massage för rygg, nacke och baksidan av benen. 
                  Efter behandlingen serveras en kylande signature alkoholfri dryck för att fullända din upplevelse.
                </p>
              </div>
              <div className="flex flex-col md:text-right text-left min-w-[160px] bg-[#f5f5f5] p-4 rounded-xl">
                <div className="text-gray-900 font-bold text-lg">Mån-Sön: 795 kr</div>
                <a
                  href="https://www.bokadirekt.se/boka-tjanst/anesis-massage-wellness-134203/summer-mojito-massage-sommarens-limited-edition-behandling-juni-augusti-2026-3468310"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Boka Summer Mojito Massage"
                  className="text-center inline-block mt-4 px-6 py-2 bg-primary text-white uppercase tracking-widest text-xs font-bold rounded-md shadow-sm hover:shadow-md transition-shadow duration-200"
                >
                  BOKA
                </a>
              </div>
            </div>
            {/* Service 5 (6) */}
            <div className="bg-white p-8 md:p-12 border-l-4 border-primary break-words flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <h3 className="text-xl font-bold text-primary uppercase tracking-wide">Klassisk helkroppsmassage/&shy;Aromamassage</h3>
                  <span className="bg-primary text-white px-3 py-1 rounded-md text-sm font-bold">55 min</span>
                </div>
                <p className="text-gray-600 leading-relaxed text-lg font-light">
                  Genom knådningar, glidningar och tryck löser massagen upp spänningar i hela kroppen och ger en behaglig och skön avslappning.
                  Du väljer själv mellan citrus massageolja (energigivande egenskaper) eller lavendel massageolja (avslappnande egenskaper).
                </p>
              </div>
              <div className="flex flex-col md:text-right text-left min-w-[160px] bg-[#f5f5f5] p-4 rounded-xl">
                <div className="text-gray-900 font-bold text-lg">Mån-Sön: 1000 kr</div>
                <a
                  href="https://www.bokadirekt.se/boka-tjanst/anesis-massage-wellness-134203/klassisk-helkroppsmassage-aromamassage-55-min-3418176"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Boka Klassisk helkroppsmassage/Aromamassage 55 min"
                  className="text-center inline-block mt-4 px-6 py-2 bg-primary text-white uppercase tracking-widest text-xs font-bold rounded-md shadow-sm hover:shadow-md transition-shadow duration-200"
                >
                  BOKA
                </a>
              </div>
            </div>
            {/* Service 1 */}
            <div className="bg-white p-8 md:p-12 border-l-4 border-primary flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <h3 className="text-xl font-bold text-primary uppercase tracking-wide">Klassisk massage</h3>
                  <span className="bg-primary text-white px-3 py-1 rounded-md text-sm font-bold">25 min</span>
                </div>
                <p className="text-gray-600 leading-relaxed text-lg font-light">
                  En klassisk djupgående ryggmassage. Spänningar i muskulaturen löses upp och blodcirkulationen ökas.
                </p>
              </div>
              <div className="flex flex-col md:text-right text-left min-w-[160px] bg-[#f5f5f5] p-4 rounded-xl">
                <div className="text-gray-900 font-bold text-lg">Mån-Fre: 525 kr</div>
                <div className="text-gray-500 text-sm mt-1">Lör-Sön: 625 kr</div>
                <a
                  href="https://www.bokadirekt.se/boka-tjanst/anesis-massage-wellness-134203/svensk-klassisk-massage-25-min-3376451"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Boka Klassisk massage 25 min"
                  className="text-center inline-block mt-4 px-6 py-2 bg-primary text-white uppercase tracking-widest text-xs font-bold rounded-md shadow-sm hover:shadow-md transition-shadow duration-200"
                >
                  BOKA
                </a>
              </div>
            </div>

            {/* Service 2 */}
            <div className="bg-white p-8 md:p-12 border-l-4 border-primary flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <h3 className="text-xl font-bold text-primary uppercase tracking-wide">Klassisk massage</h3>
                  <span className="bg-primary text-white px-3 py-1 rounded-md text-sm font-bold">40 min</span>
                </div>
                <p className="text-gray-600 leading-relaxed text-lg font-light">
                  En klassisk djupgående massage för rygg, nacke eller rygg och baksidan av benen.
                </p>
              </div>
              <div className="flex flex-col md:text-right text-left min-w-[160px] bg-[#f5f5f5] p-4 rounded-xl">
                <div className="text-gray-900 font-bold text-lg">Mån-Fre: 695 kr</div>
                <div className="text-gray-500 text-sm mt-1">Lör-Sön: 795 kr</div>
                <a
                  href="https://www.bokadirekt.se/boka-tjanst/anesis-massage-wellness-134203/svensk-klassisk-massage-40-min-3376452"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Boka Klassisk massage 40 min"
                  className="text-center inline-block mt-4 px-6 py-2 bg-primary text-white uppercase tracking-widest text-xs font-bold rounded-md shadow-sm hover:shadow-md transition-shadow duration-200"
                >
                  BOKA
                </a>
              </div>
            </div>

            {/* Service 3 */}
            <div className="bg-white p-8 md:p-12 border-l-4 border-primary flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <h3 className="text-xl font-bold text-primary uppercase tracking-wide">Fot och underbensmassage</h3>
                  <span className="bg-primary text-white px-3 py-1 rounded-md text-sm font-bold">20 min</span>
                </div>
                <p className="text-gray-600 leading-relaxed text-lg font-light">
                  Massagen ökar cirkulationen, vilket kan motverka tyngd, svullnad och trötthet i benen.
                </p>
              </div>
              <div className="flex flex-col md:text-right text-left min-w-[160px] bg-[#f5f5f5] p-4 rounded-xl">
                <div className="text-gray-900 font-bold text-lg">Mån-Fre: 450 kr</div>
                <div className="text-gray-500 text-sm mt-1">Lör-Sön: 550 kr</div>
                <a
                  href="https://www.bokadirekt.se/boka-tjanst/anesis-massage-wellness-134203/fot-och-underbensmassage-20-min-3380220"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Boka Fot och underbensmassage 20 min"
                  className="text-center inline-block mt-4 px-6 py-2 bg-primary text-white uppercase tracking-widest text-xs font-bold rounded-md shadow-sm hover:shadow-md transition-shadow duration-200"
                >
                  BOKA
                </a>
              </div>
            </div>

            {/* Service 4 */}
            <div className="bg-white p-8 md:p-12 border-l-4 border-primary flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <h3 className="text-xl font-bold text-primary uppercase tracking-wide">Antik grekisk massage</h3>
                  <span className="bg-primary text-white px-3 py-1 rounded-md text-sm font-bold">70 min</span>
                </div>
                <p className="text-gray-600 leading-relaxed text-lg font-light">
                  Antik grekisk massage är byggd på olika tekniker som torrmassage, massage med varmt ljus samt koppningsmassage.
                  Den stöds av Hippokrates, medicinens fader. En unik holistisk upplevelse, rekommenderas till alla som vill återuppliva kropp och själ samt ge sig en resa till antikens Grekland.
                  Under 70 min ingår en helkroppsmassage, ansiktsmassage och hårbottensmassage.
                </p>
              </div>
              <div className="flex flex-col md:text-right text-left min-w-[160px] bg-[#f5f5f5] p-4 rounded-xl">
                <div className="text-gray-900 font-bold text-lg">Mån-Fre: 1195 kr</div>
                <div className="text-gray-500 text-sm mt-1">Lör-Sön: 1295 kr</div>
                <a
                  href="https://www.bokadirekt.se/boka-tjanst/anesis-massage-wellness-134203/antik-grekisk-massage-3376473"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Boka Antik grekisk massage 70 min"
                  className="text-center inline-block mt-4 px-6 py-2 bg-primary text-white uppercase tracking-widest text-xs font-bold rounded-md shadow-sm hover:shadow-md transition-shadow duration-200"
                >
                  BOKA
                </a>
              </div>
            </div>
            
          </div>
          {/* Discounts Info */}
          <div className="mt-24 grid md:grid-cols-2 gap-6 px-12">
            <div className="bg-white p-8 border-l-4 border-primary">
              <h4 className="font-bold text-lg text-primary mb-3 uppercase tracking-wide">Tipsa en vän</h4>
              <p className="text-gray-600 font-light text-lg">
                Om du får någon annan att boka en behandling hos oss, får du <span className="font-bold text-primary">10% rabatt</span> på din nästa behandling.
              </p>
            </div>
            <div className="bg-white p-8 border-l-4 border-primary">
              <h4 className="font-bold text-lg text-primary mb-3 uppercase tracking-wide">Student & Pensionär</h4>
              <p className="text-gray-600 font-light text-lg">
                Vi erbjuder alltid <span className="font-bold text-primary">10% rabatt</span> för studenter och pensionärer.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section id="samarbetspartner" className="py-32 bg-gradient-to-b from-[#f5f5f5] to-white">
        <div className="container mx-auto px-6 lg:px-12 max-w-4xl text-center">
          <h2 className="text-3xl font-light text-primary uppercase tracking-widest mb-6 break-all">SAMARBETSPARTNER</h2>
          <p className="mb-6 text-lg font-light">
            Hos oss kan du använda dina Friskvårdspengar. Det går bra att betala hos oss på plats och få friskvård på kvittot.
            <br /><br /> Du kan betala med Epassi & Benifex.
          </p>
          <div className="w-24 h-1 bg-gray-100 mx-auto rounded-full"></div>
          <div className='flex flex-col md:flex-row md:gap-16 align-center justify-center'>
            <img src="/Epassi-Logo.webp" alt="Epassi" className="mx-auto md:mx-0 mt-6 h-20 w-auto" />
            <img src="/benifex-logo.png" alt="Benifex" className="mx-auto md:mx-0 mt-6 h-20 w-auto" />
          </div>
        </div>
      </section>

      {/* Info & Contact Section */}
      <section id="kontakt" className="py-24 bg-white border-t border-gray-100">
        <div className="container mx-auto px-6 lg:px-12 max-w-6xl">
          <div className="grid md:grid-cols-2 gap-20">

            {/* Contact Info */}
            <div className="space-y-10">
              <h2 className="text-2xl font-light text-primary uppercase tracking-widest mb-8">KONTAKT & HITTA</h2>

              <div className="flex items-start gap-6 group">
                <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center text-secondary">
                  <MapPin className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2 tracking-wide">ADRESS</h4>
                  <p className="text-gray-600 text-lg font-light">Mimmi Ekholms Plats 13<br />754 20 Uppsala</p>
                </div>
              </div>

              <div className="flex items-start gap-6 group">
                <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center text-secondary">
                  <Phone className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2 tracking-wide">TELEFON</h4>
                  <p className="text-gray-600 text-lg font-light">073-251 77 31</p>
                </div>
              </div>

              <div className="flex items-start gap-6 group">
                <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center text-secondary">
                  <Mail className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2 tracking-wide">EMAIL</h4>
                  <p className="text-gray-600 text-lg font-light">support@anesis.se</p>
                </div>
              </div>
            </div>

            {/* Opening Hours */}
            <div className="space-y-10">
              <h2 className="text-2xl font-light text-primary uppercase tracking-widest mb-8">ÖPPETTIDER</h2>

              <div className="space-y-6 text-lg">
                <div className="flex justify-between border-b border-gray-100 pb-4">
                  <span className="text-gray-600 font-light">Måndag–fredag</span>
                  <span className="font-bold text-primary">17:00–20:30</span>
                </div>
                <div className="flex justify-between border-b border-gray-100 pb-4">
                  <span className="text-gray-600 font-light">Lördag–söndag</span>
                  <span className="font-bold text-primary">11:30–17:00</span>
                </div>
              </div>

              <div className="mt-8 bg-blue-50 p-6 rounded-md border border-blue-100 text-center">
                <p className="text-gray-600 text-sm italic leading-relaxed">
                  Avbokning: Vid förhinder måste avbokning ske 24 timmar innan behandlingstillfället. Ej avbokad tid debiteras fullt pris.
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Footer - Minimal */}
      <footer className="bg-primary text-white py-16 text-center">
        <div className="container mx-auto px-6">
          <img src="/anesis-long-svg.svg" alt="Anesis" className="h-12 w-auto brightness-0 invert opacity-90 mx-auto mb-8" />
          <div className="w-16 h-0.5 bg-blue-400 mx-auto mb-8 opacity-50"></div>
          <p className="text-blue-200/60 text-sm tracking-widest uppercase">
            &copy; {new Date().getFullYear()} Anesis Massage & Wellness
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;
